import { Skeleton } from '@design-system/Skeleton';

interface MyFoodDetailsSkeletonProps {
  'data-testid'?: string;
}

export const MyFoodDetailsSkeleton = ({ 'data-testid': testId }: MyFoodDetailsSkeletonProps = {}) => {
  return (
    <section data-testid={testId}>
      <div 
        data-testid={`${testId}-container`}
        className="mx-auto container p-4 md:px-8 lg:px-24 lg:py-6 max-w-[960px]"
      >
        <div 
          data-testid={`${testId}-navbar`}
          className="md:-ml-5"
        ></div>

        <div 
          data-testid={`${testId}-basicDetailsSection`}
          className="md:pt-16 pt-32 flex flex-col md:flex-row items-center md:items-end md:justify-between"
        >
          <div className="mr-5">
            <Skeleton 
              data-testid={`${testId}-titleSkeleton`}
              className="h-12 w-52 mb-4" 
            />
            <div 
              data-testid={`${testId}-rankAndServingsSkeleton`}
              className="flex gap-2"
            >
              <Skeleton 
                data-testid={`${testId}-rankSkeleton`}
                className="h-6 w-24" 
              />
              <Skeleton 
                data-testid={`${testId}-servingsSkeleton`}
                className="h-6 w-24" 
              />
            </div>
          </div>
          <div className="mt-4 flex flex-row justify-center md:justify-end">
            <Skeleton 
              data-testid={`${testId}-imageSkeleton`}
              className="w-72 lg:w-96 h-72 rounded-lg" 
            />
          </div>
        </div>

        <div 
          data-testid={`${testId}-badgesSection`}
          className="flex justify-center md:justify-start gap-4 mt-12"
        >
          {Array.from({ length: 2 }).map((_, index) => (
            <div
              key={index}
              data-testid={`${testId}-badge-${index}`}
              className="w-40 h-40 rounded-2xl bg-white shadow-sm flex flex-col items-center justify-center p-4"
            >
              <Skeleton className="w-12 h-12 rounded-full mb-4" />
              <Skeleton className="w-24 h-4" />
            </div>
          ))}
        </div>

        <div 
          data-testid={`${testId}-whyThisFoodSection`}
          className="pt-12"
        >
          <Skeleton 
            data-testid={`${testId}-whyThisFoodTitle`}
            className="h-8 w-72 mb-4" 
          />
          <Skeleton 
            data-testid={`${testId}-whyThisFoodDescription`}
            className="h-6 w-96 mb-8" 
          />
        </div>

        <div 
          data-testid={`${testId}-categoryDetailsSection`}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6"
        >
          <div 
            data-testid={`${testId}-categoryCard-0`}
            className="bg-white rounded-lg p-6 shadow-sm"
          >
            <Skeleton 
              data-testid={`${testId}-categoryCardTitle-0`}
              className="h-7 w-48 mb-6" 
            />

            <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-4">
              <div className="flex-1">
                <Skeleton 
                  data-testid={`${testId}-categoryCardItemTitle-0-0`}
                  className="h-6 w-3/4 mb-2" 
                />
                <Skeleton 
                  data-testid={`${testId}-categoryCardItemRank-0-0`}
                  className="h-4 w-16" 
                />
              </div>
              <Skeleton 
                data-testid={`${testId}-categoryCardItemScore-0-0`}
                className="h-6 w-6" 
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Skeleton 
                  data-testid={`${testId}-categoryCardItemTitle-0-1`}
                  className="h-6 w-3/4 mb-2" 
                />
                <Skeleton 
                  data-testid={`${testId}-categoryCardItemRank-0-1`}
                  className="h-4 w-16" 
                />
              </div>
              <Skeleton 
                data-testid={`${testId}-categoryCardItemScore-0-1`}
                className="h-6 w-6" 
              />
            </div>
          </div>

          <div 
            data-testid={`${testId}-categoryCard-1`}
            className="bg-white rounded-lg p-6 shadow-sm"
          >
            <Skeleton 
              data-testid={`${testId}-categoryCardTitle-1`}
              className="h-7 w-48 mb-6" 
            />

            <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-4">
              <div className="flex-1">
                <Skeleton 
                  data-testid={`${testId}-categoryCardItemTitle-1-0`}
                  className="h-6 w-3/4 mb-2" 
                />
                <Skeleton 
                  data-testid={`${testId}-categoryCardItemRank-1-0`}
                  className="h-4 w-16" 
                />
              </div>
              <Skeleton 
                data-testid={`${testId}-categoryCardItemScore-1-0`}
                className="h-6 w-6" 
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Skeleton 
                  data-testid={`${testId}-categoryCardItemTitle-1-1`}
                  className="h-6 w-3/4 mb-2" 
                />
                <Skeleton 
                  data-testid={`${testId}-categoryCardItemRank-1-1`}
                  className="h-4 w-16" 
                />
              </div>
              <Skeleton 
                data-testid={`${testId}-categoryCardItemScore-1-1`}
                className="h-6 w-6" 
              />
            </div>
          </div>
        </div>
      </div>
      <div 
        data-testid={`${testId}-footerSection`}
        className="mt-4 flex justify-center"
      >
        <Skeleton 
          data-testid={`${testId}-footerSkeleton`}
          className="h-80 w-full" 
        />
      </div>
    </section>
  );
};